const CITYSPRINT_NETWORK_CODE = 'CITYSPRINT_SAMEDAY';

const pickPrice = row => {
  if (row == null || typeof row !== 'object') {
    return undefined;
  }
  const n = v => (typeof v === 'number' && !Number.isNaN(v) ? v : Number(v));
  return n(
    row.price ??
      row.amount ??
      row.totalPrice ??
      row.total ??
      row.servicePrice ??
      row.exVatPrice ??
      row.netPrice
  );
};

const num = v => {
  const x = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(x) ? x : undefined;
};

const CAPACITY_KEYS = [
  'maxLengthCM',
  'maxWidthCM',
  'maxHeightCM',
  'maxWeightKG',
];

const vehicleCapacityFrom = cap => {
  if (cap == null || typeof cap !== 'object') {
    return undefined;
  }
  const out = {};
  CAPACITY_KEYS.forEach(key => {
    const v = num(cap[key]);
    if (v != null) {
      out[key] = v;
    }
  });
  return Object.keys(out).length ? out : undefined;
};

const pickExtendedVehicleDetails = vehicleOrRow => {
  const ext = vehicleOrRow?.extendedVehicleDetails;
  if (ext == null || typeof ext !== 'object') {
    return {};
  }

  const vehicleIconUrl =
    typeof ext.vehicleIcon === 'string' ? ext.vehicleIcon.trim() : undefined;
  const vehicleCapacity = vehicleCapacityFrom(ext.vehicleCapacity);

  return {
    ...(vehicleIconUrl ? { vehicleIconUrl } : {}),
    ...(vehicleCapacity ? { vehicleCapacity } : {}),
  };
};

const mergeVehicleFields = (base, vehicleOrRow) => {
  const extra = pickExtendedVehicleDetails(vehicleOrRow);
  return { ...base, ...extra };
};

const serviceCodeFrom = service => {
  if (typeof service?.serviceCode === 'string' && service.serviceCode.trim()) {
    return service.serviceCode.trim();
  }
  return CITYSPRINT_NETWORK_CODE;
};

const networkDescFromService = (service, vehicle) => {
  const vehicleLabel =
    typeof vehicle?.vehicleDescription === 'string'
      ? vehicle.vehicleDescription
      : '';
  const desc =
    (typeof service?.serviceDescription === 'string' &&
      service.serviceDescription) ||
    'Same Day delivery';
  return vehicleLabel ? `${desc} (${vehicleLabel})` : desc;
};

const legacyNetworkCode = (row, index) => {
  if (typeof row?.serviceCode === 'string' && row.serviceCode) {
    return row.serviceCode;
  }
  if (typeof row?.code === 'string' && row.code) {
    return row.code;
  }
  return index > 0
    ? `${CITYSPRINT_NETWORK_CODE}_${index}`
    : CITYSPRINT_NETWORK_CODE;
};

const legacyNetworkDesc = row => {
  if (typeof row?.description === 'string' && row.description) {
    return row.description;
  }
  if (typeof row?.serviceName === 'string' && row.serviceName) {
    return row.serviceName;
  }
  if (typeof row?.name === 'string' && row.name) {
    return row.name;
  }
  return 'Same Day delivery';
};

const parcelVehicleKeyFrom = (svcCode, vehicle) => {
  if (typeof vehicle?.vehicleId === 'string' && vehicle.vehicleId.trim()) {
    return vehicle.vehicleId.trim();
  }
  if (typeof vehicle?.id === 'string' && vehicle.id.trim()) {
    return vehicle.id.trim();
  }
  return `${svcCode}-${vehicle?.vehicleDescription ?? 'vehicle'}`;
};

// eslint-disable-next-line complexity -- field merge from APIM service + vehicle
const mapServiceAndVehicleToNetwork = (service, vehicle) => {
  const pb = service?.priceBreakdown ?? {};
  const totalInc = num(pb.totalBookingPrice);
  const totalVat = num(pb.totalTaxAmount);
  let preTax = num(pb.preTaxAmount);
  if (
    preTax === undefined &&
    Number.isFinite(totalInc) &&
    Number.isFinite(totalVat)
  ) {
    preTax = Number((totalInc - totalVat).toFixed(2));
  }
  const withVat = totalInc ?? preTax;
  const safeEx = preTax ?? 0;
  const safeInc = withVat ?? safeEx;

  const svcCode = serviceCodeFrom(service);

  const sectionTitle =
    (typeof service?.serviceDescription === 'string' &&
      service.serviceDescription.trim()) ||
    'Same Day delivery';

  const vehicleDescription =
    typeof vehicle?.vehicleDescription === 'string'
      ? vehicle.vehicleDescription.trim()
      : undefined;

  const vehicleType =
    typeof vehicle?.vehicleType === 'string' && vehicle.vehicleType.trim()
      ? vehicle.vehicleType.trim()
      : undefined;

  const apiServiceCode =
    typeof service?.serviceCode === 'string' && service.serviceCode.trim()
      ? service.serviceCode.trim()
      : undefined;

  const latestCollectionTime =
    typeof service?.latestCollectionTime === 'string'
      ? service.latestCollectionTime.trim()
      : undefined;
  const latestDeliveryTime =
    typeof service?.latestDeliveryTime === 'string'
      ? service.latestDeliveryTime.trim()
      : undefined;

  return mergeVehicleFields(
    {
      networkCode: String(svcCode),
      networkDesc: networkDescFromService(service, vehicle),
      servicePrice: safeEx,
      servicePriceWithVatRate: safeInc,
      parcelSectionTitle: sectionTitle,
      parcelVehicleKey: parcelVehicleKeyFrom(svcCode, vehicle),
      vehicleDescription,
      serviceDescription: sectionTitle,
      ...(apiServiceCode ? { serviceCode: apiServiceCode } : {}),
      ...(vehicleType ? { vehicleType } : {}),
      ...(latestCollectionTime ? { latestCollectionTime } : {}),
      ...(latestDeliveryTime ? { latestDeliveryTime } : {}),
    },
    vehicle
  );
};

const toLegacyNetworkRow = (row, index) => {
  const price = pickPrice(row);
  const safePrice = Number.isFinite(price) ? price : 0;
  const code = legacyNetworkCode(row, index);

  return mergeVehicleFields(
    {
      networkCode: String(code),
      networkDesc: legacyNetworkDesc(row),
      servicePrice: safePrice,
      servicePriceWithVatRate: safePrice,
      parcelSectionTitle: legacyNetworkDesc(row),
      parcelVehicleKey: String(code),
    },
    row
  );
};

export const getVehicleQuotes = data => {
  if (data == null) {
    return [];
  }
  if (Array.isArray(data.data?.quotes)) {
    return data.data.quotes;
  }
  if (Array.isArray(data.quotes)) {
    return data.quotes;
  }
  return [];
};

export const mapCitysprintVehicleToNetworkRows = vehicle => {
  if (vehicle == null || typeof vehicle !== 'object') {
    return [];
  }
  const services = vehicle?.availableServices;
  if (Array.isArray(services)) {
    if (!services.length) {
      return [];
    }
    return services.map(service =>
      mapServiceAndVehicleToNetwork(service, vehicle)
    );
  }
  return [toLegacyNetworkRow(vehicle, 0)];
};

export const mapCitysprintQuoteToNetworks = data => {
  const vehicleQuotes = getVehicleQuotes(data);

  if (vehicleQuotes.length) {
    const rows = [];

    vehicleQuotes.forEach(vehicle => {
      rows.push(...mapCitysprintVehicleToNetworkRows(vehicle));
    });

    return rows;
  }

  if (data == null) {
    return [];
  }

  if (Array.isArray(data)) {
    return data.map(toLegacyNetworkRow);
  }

  if (Array.isArray(data.quoteOptions)) {
    return data.quoteOptions.map(toLegacyNetworkRow);
  }

  if (Array.isArray(data.services)) {
    return data.services.map(toLegacyNetworkRow);
  }

  const singlePrice = pickPrice(data);
  if (singlePrice !== undefined && Number.isFinite(singlePrice)) {
    return [toLegacyNetworkRow(data, 0)];
  }

  return [];
};
